/**
 * Typography style templates and editor configuration
 */

export interface TypographyStyle {
  id: string;
  name: string;
  description: string;
  fontFamily: string;
  fontSize: number; // in px
  lineHeight: number;
  paragraphSpacing: number; // in em
  headingScale: number; // multiplier for headings
  headingTopMargin: number; // in em
  headingBottomMargin: number; // in em
  sectionSpacing: number; // in em - extra space between major sections
  tableBorders?: boolean; // whether to show table borders
}

export const TYPOGRAPHY_TEMPLATES: TypographyStyle[] = [
  {
    id: 'professional',
    name: 'Professional',
    description: 'Clean sans-serif, perfect for business documents',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 16,
    lineHeight: 1.6,
    paragraphSpacing: 1.2,
    headingScale: 1.5,
    headingTopMargin: 1.5,
    headingBottomMargin: 0.5,
    sectionSpacing: 2.0,
    tableBorders: true,
  },
  {
    id: 'academic',
    name: 'Academic',
    description: 'Traditional serif, ideal for formal reports',
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontSize: 16,
    lineHeight: 1.8,
    paragraphSpacing: 1.5,
    headingScale: 1.4,
    headingTopMargin: 2.0,
    headingBottomMargin: 0.75,
    sectionSpacing: 2.5,
    tableBorders: true,
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Contemporary and readable',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 15,
    lineHeight: 1.7,
    paragraphSpacing: 1.3,
    headingScale: 1.6,
    headingTopMargin: 1.2,
    headingBottomMargin: 0.6,
    sectionSpacing: 1.8,
    tableBorders: true,
  },
  {
    id: 'compact',
    name: 'Compact',
    description: 'Space-efficient for dense content',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 14,
    lineHeight: 1.5,
    paragraphSpacing: 0.8,
    headingScale: 1.3,
    headingTopMargin: 1.0,
    headingBottomMargin: 0.4,
    sectionSpacing: 1.2,
    tableBorders: true,
  },
  {
    id: 'casual',
    name: 'Casual',
    description: 'Friendly and approachable',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: 16,
    lineHeight: 1.7,
    paragraphSpacing: 1.4,
    headingScale: 1.5,
    headingTopMargin: 1.6,
    headingBottomMargin: 0.6,
    sectionSpacing: 2.2,
    tableBorders: true,
  },
];

export const DEFAULT_TYPOGRAPHY: TypographyStyle = TYPOGRAPHY_TEMPLATES[0]; // Professional

// Font family options for custom editor
export const FONT_FAMILY_OPTIONS = [
  { value: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', label: 'System Sans' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Serif' },
  { value: 'Inter, system-ui, sans-serif', label: 'Inter' },
  { value: '"Courier New", Courier, monospace', label: 'Monospace' },
  { value: 'ui-rounded, "SF Pro Rounded", sans-serif', label: 'Rounded' },
];
